import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import { loginUser, updateRestaurantDetails } from '../store/slices/authSlice';
import authService from '../services/authService';
import { API_ENDPOINTS } from '../config/api';
import axios from '../config/axiosConfig';
import appLogger from '../utils/appLogger';
import QRScanner from './QRScanner';
import { motion } from 'framer-motion';

const Login = () => {
    const [emailOrPhone, setEmailOrPhone] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showQRScanner, setShowQRScanner] = useState(false);
    const dispatch = useAppDispatch();
    const { loading: authLoading, error: authError } = useAppSelector(state => state.auth);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const restaurantIdFromUrl = searchParams.get('restaurantId');

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        
        try {
            const result = await authService.login(emailOrPhone, password);
            
            if (result.user) {
                // Check if password reset is required BEFORE dispatching to Redux
                if (result.requiresPasswordReset || result.user.isFirstLogin) {
                    // Store user data temporarily for password reset
                    localStorage.setItem('authToken', result.token);
                    localStorage.setItem('userData', JSON.stringify(result.user));
                    if (result.restaurant) {
                        localStorage.setItem('restaurantData', JSON.stringify(result.restaurant));
                    }
                    
                    // Store restaurantId from URL if present
                    const redirectRestaurantId = restaurantIdFromUrl || result.user.restaurantId;
                    const email = result.user.email || emailOrPhone;
                    // Redirect to password reset page with email and restaurantId
                    navigate(`/reset-password?restaurantId=${redirectRestaurantId}&firstLogin=true&email=${encodeURIComponent(email)}`);
                    return;
                }

                // Redux must use the same identifier shape as the API (customers: phoneNumber, not email).
                const reduxCredentials =
                    result.user.role === 'customer'
                        ? {
                              phoneNumber: result.user.phoneNumber || String(emailOrPhone).replace(/\D/g, '').slice(-10),
                              password,
                          }
                        : {
                              email: result.user.email || emailOrPhone,
                              password,
                          };
                const loginResult = await dispatch(loginUser(reduxCredentials));
                
                if (loginUser.fulfilled.match(loginResult)) {
                    // For staff, fetch restaurant data if not provided
                    if (result.user.role === 'staff' && !result.restaurant && result.user.restaurantId) {
                        try {
                            const restaurantResponse = await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(result.user.restaurantId));
                            if (restaurantResponse.data) {
                                localStorage.setItem('restaurantData', JSON.stringify(restaurantResponse.data));
                                // Update Redux store
                                dispatch(updateRestaurantDetails(restaurantResponse.data));
                            }
                        } catch (err) {
                            console.error('Failed to fetch restaurant data:', err);
                        }
                    }
                    
                    // Store restaurantId from URL if present (for staff login links)
                    if (restaurantIdFromUrl && result.user.role === 'staff') {
                        localStorage.setItem('restaurantId', restaurantIdFromUrl);
                    }
                    
                    // Redirect based on user role
                    switch (result.user.role) {
                        case 'superadmin':
                            navigate('/superadmin');
                            break;
                        case 'restaurantadmin':
                            navigate('/restaurant');
                            break;
                        case 'staff':
                            // Check staff role name to redirect to appropriate dashboard
                            const roleName = result.user.roleName || result.user.role?.name;
                            if (roleName === 'COOK' || roleName === 'Cook' || roleName === 'cook') {
                                navigate('/chef');
                            } else {
                                // Default to waiter dashboard for other staff roles
                                navigate('/waiter');
                            }
                            break;
                        case 'customer':
                            navigate('/customer');
                            break;
                        case 'companyemployee':
                            navigate('/employee/dashboard');
                            break;
                        default:
                            navigate('/');
                    }
                    appLogger.log('Login success', { action: 'login', role: result.user.role });
                } else {
                    setError(loginResult.payload || 'Login failed');
                }
            }
        } catch (error) {
            console.error('Login failed:', error);
            const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message?.includes('Network'));
            const message = isNetworkError
                ? 'Cannot reach server. Check internet connection. If using the app, try again or use the website.'
                : (error.response?.data?.message || error.message || 'Login failed. Please check your credentials.');
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    // Show auth error if any
    useEffect(() => {
        if (authError) {
            setError(authError);
        }
    }, [authError]);

    const handleQRScan = (data) => {
        setShowQRScanner(false);
        const { restaurantId, tableId } = data;
        if (restaurantId && tableId) {
            navigate(`/menu/${restaurantId}/${tableId}`);
        }
    };

    const inputClass = "w-full px-3 py-2.5 sm:px-4 sm:py-3 bg-stone-50 border border-stone-200 rounded-lg sm:rounded-xl text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300 text-sm sm:text-base";

    return (
        <div className="min-h-screen bg-stone-50 text-stone-900 flex items-center justify-center px-3 sm:px-4 py-6 overflow-hidden">
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-amber-100/40 via-transparent to-transparent" />
                <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-[60vmax] h-[60vmax] bg-amber-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(251,191,36,0.12),transparent)]" />
            </div>

            <div className="relative z-10 max-w-md w-full">
                <motion.div
                    className="text-center mb-4 sm:mb-6 md:mb-8"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <img src="/logo-bright.svg" alt="RestroAI" className="h-10 sm:h-12 md:h-14 w-auto mx-auto mb-3 sm:mb-6" />
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900 mb-1 sm:mb-2">Welcome Back</h2>
                    <p className="text-stone-600 text-sm sm:text-base">Sign in to your RestroAI account</p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 }}
                    className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-stone-200/80 shadow-sm"
                >
                    <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5 md:space-y-6">
                        <div>
                            <label htmlFor="emailOrPhone" className="block text-xs sm:text-sm font-semibold text-stone-700 mb-1.5 sm:mb-2">
                                Email or Phone Number
                            </label>
                            <input
                                id="emailOrPhone"
                                type="text"
                                placeholder="Email or phone"
                                value={emailOrPhone}
                                onChange={(e) => setEmailOrPhone(e.target.value)}
                                className={inputClass}
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-stone-700 mb-1.5 sm:mb-2">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={inputClass}
                                required
                            />
                        </div>

                        <div className="flex items-center justify-between gap-2">
                            <label className="flex items-center cursor-pointer shrink-0">
                                <input type="checkbox" className="rounded border-stone-300 bg-stone-50 text-amber-500 focus:ring-amber-500 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span className="ml-1.5 sm:ml-2 text-xs sm:text-sm text-stone-600">Remember me</span>
                            </label>
                            <Link to="/forgot-password" className="text-xs sm:text-sm text-amber-600 hover:text-amber-700 font-medium transition-colors shrink-0">
                                Forgot password?
                            </Link>
                        </div>

                        {error && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                                <div className="flex items-center space-x-2">
                                    <span className="text-amber-600 shrink-0">⚠️</span>
                                    <span className="text-stone-700 text-xs sm:text-sm">{error}</span>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 shadow-lg shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent mr-2"></div>
                                    <span className="text-sm sm:text-base">Signing in...</span>
                                </div>
                            ) : (
                                'Sign In'
                            )}
                        </button>

                        <Link
                            to="/contact-us"
                            className="w-full inline-flex items-center justify-center bg-amber-50 border border-amber-200 hover:bg-amber-100/80 text-amber-700 hover:text-amber-800 py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300"
                        >
                            Start Free Trial
                        </Link>

                        <Link
                            to="/restaurants"
                            className="w-full inline-flex items-center justify-center gap-1.5 sm:gap-2 border border-stone-200 hover:border-stone-300 hover:bg-stone-50 text-stone-700 py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300"
                        >
                            🍽️ Browse restaurants
                        </Link>

                        <button
                            type="button"
                            onClick={() => setShowQRScanner(true)}
                            className="md:hidden w-full border border-stone-200 hover:border-stone-300 hover:bg-stone-50 text-stone-700 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            <span className="text-base">📱</span>
                            Scan Restaurant QR Code
                        </button>
                    </form>
                </motion.div>

                <QRScanner
                    isOpen={showQRScanner}
                    onClose={() => setShowQRScanner(false)}
                    onScan={handleQRScan}
                />

                <div className="text-center mt-4 sm:mt-6">
                    <Link to="/" className="text-stone-500 hover:text-amber-600 text-xs sm:text-sm font-medium transition-colors">
                        ← Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Login;
